import { apiRequest } from './client';
import { toUserFacingError } from '../utils/errorMessages';

export interface User {
  id: number;
  email: string;
  full_name?: string | null;
  is_active: boolean;
  created_at: string;
  onboarding_completed: boolean;
  country?: string | null;
  state?: string | null;
  postal_code?: string | null;
  organization_name?: string | null;
  industry?: string | null;
  organization_type?: string | null;
  job_role?: string | null;
  primary_jurisdictions?: string | null;
  regulation_types?: string | null;
  use_cases?: string | null;
  team_size?: string | null;
  newsletter_weekly_enabled?: boolean;
  newsletter_digest_frequency?: 'none' | 'daily' | 'weekly';
  newsletter_weekly_day?: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  is_enterprise_member?: boolean;
  /** SRS #39: null until the guided tour is finished or skipped */
  tour_completed_at?: string | null;
  enterprise_owner_id?: number | null;
  enterprise_member_id?: number | null;
  enterprise_allowed_features?: string[];
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  cognito_username?: string;
  user: User;
}

export interface SignupPayload {
  email: string;
  password: string;
  full_name?: string;
  captcha_token?: string | null;
  /** Honeypot (SRS #110): hidden field; must stay empty. */
  website?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface CaptchaConfig {
  provider: 'none' | 'turnstile' | string;
  site_key: string | null;
  google_sign_in?: boolean;   // the server has a Google OAuth client id configured
}

export async function fetchCaptchaConfig(): Promise<CaptchaConfig> {
  return apiRequest<CaptchaConfig>('/api/v1/auth/captcha', { method: 'GET' });
}

export interface UpdateProfilePayload {
  full_name: string;
}

export interface OnboardingPayload {
  country?: string;
  state?: string;
  postal_code?: string;
  organization_name?: string;
  industry?: string;
  organization_type?: string;
  job_role?: string;
  primary_jurisdictions?: string[];
  regulation_types?: string[];
  use_cases?: string[];
  team_size?: string;
}

export interface ForgotPasswordConfirmPayload {
  email: string;
  code: string;
  new_password: string;
}

export interface SignupResponse {
  message: string;
  email: string;
  requires_verification: boolean;
}

export interface VerifyEmailPayload {
  email: string;
  otp: string;
}

export async function signup(payload: SignupPayload): Promise<SignupResponse> {
  return apiRequest<SignupResponse>('/api/v1/auth/signup', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function verifyEmailOtp(payload: VerifyEmailPayload): Promise<TokenResponse & { message: string; verified: boolean }> {
  return apiRequest<TokenResponse & { message: string; verified: boolean }>('/api/v1/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function resendOtp(email: string): Promise<{ detail: string }> {
  return apiRequest<{ detail: string }>('/api/v1/auth/resend-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export interface MfaChallenge {
  mfa_required: true;
  email: string;
}

export async function verifyLoginMfa(payload: { email: string; otp: string }): Promise<TokenResponse & { message: string }> {
  return apiRequest<TokenResponse & { message: string }>('/api/v1/auth/login/verify-mfa', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(payload),
  });
}

export async function login(payload: LoginPayload): Promise<TokenResponse | MfaChallenge> {
  const body = new URLSearchParams();
  body.append('username', payload.email);
  body.append('password', payload.password);

  const baseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
  const response = await fetch(`${baseUrl}/api/v1/auth/login`, {
    method: 'POST',
    credentials: 'include',  // accept httpOnly cookie from server
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });

  if (!response.ok) {
    const raw = await response.text().catch(() => 'Login failed');
    // Enterprise SSO (SRS #74–#76): the workspace enforces SSO for this domain.
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.detail?.code === 'sso_required') {
        const err = new Error(parsed.detail.message || 'Your organisation signs in with SSO.') as Error & { ssoConnectionId?: number };
        err.ssoConnectionId = parsed.detail.connection_id;
        throw err;
      }
    } catch (e) { if ((e as { ssoConnectionId?: number }).ssoConnectionId) throw e; }
    const friendly = toUserFacingError(raw, 'Login failed');
    if (friendly.includes('temporarily unavailable') && raw?.includes('<html')) {
      console.error('Login failed with HTML error page:', raw.slice(0, 200));
    }
    throw new Error(friendly);
  }

  return (await response.json()) as TokenResponse | MfaChallenge;
}

export async function changePassword(token: string, payload: ChangePasswordPayload): Promise<void> {
  await apiRequest<void>('/api/v1/auth/change-password', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function fetchCurrentUser(token: string): Promise<User> {
  return apiRequest<User>('/api/v1/users/me', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function updateProfile(token: string, payload: UpdateProfilePayload): Promise<User> {
  return apiRequest<User>('/api/v1/users/me', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function completeOnboarding(token: string, payload: OnboardingPayload): Promise<User> {
  return apiRequest<User>('/api/v1/users/onboarding', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function requestPasswordReset(email: string, captchaToken?: string | null): Promise<{ detail: string }> {
  return apiRequest<{ detail: string }>('/api/v1/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email, captcha_token: captchaToken ?? undefined }),
  });
}

export async function confirmPasswordReset(payload: ForgotPasswordConfirmPayload): Promise<{ detail: string }> {
  return apiRequest<{ detail: string }>('/api/v1/auth/forgot-password/confirm', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deleteAccount(token: string): Promise<{ detail: string }> {
  return apiRequest<{ detail: string }>('/api/v1/users/me', {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function confirmDeleteAccount(token: string): Promise<{ detail: string }> {
  return apiRequest<{ detail: string }>('/api/v1/users/confirm-delete', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}
